/**
 * useFileUpload Hook
 *
 * Manages file uploads with persistence for equipment documents
 *
 * CRITICAL: All functions are memoized with useCallback to prevent
 * infinite re-render loops when used in useEffect dependencies.
 *
 * Usage:
 *   const { documents, uploading, uploadFile, deleteFile, downloadFile } = useFileUpload(equipmentId);
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useStorageState } from './useStorageState';
import { processFile, downloadFile as downloadFileUtil } from '../utils/fileUpload';

export const useFileUpload = (relatedTo, relatedType = 'equipment') => {
  // Get all documents from storage (vessel-scoped)
  const { value: allDocuments = [], setValue: setAllDocuments } = useStorageState('equipment_documents', []);
  const [uploading, setUploading] = useState(false);

  // Filter documents for the current item (equipment, defect, etc.)
  // Use useMemo to avoid recalculating on every render
  const documents = useMemo(() => {
    if (!relatedTo) return [];
    return allDocuments.filter(doc => doc.relatedTo === relatedTo);
  }, [relatedTo, allDocuments]);

  // CRITICAL: useCallback ensures this function has a stable reference
  // This prevents infinite loops when used in useEffect dependencies
  const uploadFile = useCallback(async (file) => {
    if (!relatedTo) {
      throw new Error('Cannot upload file: relatedTo ID is required');
    }

    // FT-345: Photo evidence now lives in the FT-340 pipeline
    // (<PhotoAttachment> → IndexedDB Blob store), NOT in equipment_documents.
    // If a user drags an image into the old FileUpload area, redirect them.
    // Guard by MIME type first, then by extension as a fallback (some browsers
    // report empty file.type for HEIC).
    const isImageMime = file?.type?.startsWith('image/');
    const isImageExt = /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(file?.name || '');
    if (isImageMime || isImageExt) {
      throw new Error(
        'Please use the Photos section above for images. The Upload Document area is for PDFs and Word documents only.'
      );
    }

    setUploading(true);
    try {
      // Process the file (PDF / DOCX only — images are rejected above).
      const processed = await processFile(file, {
        maxSizeKB: 2048, // 2 MB cap for non-image documents
        compress: false
      });

      // Create document object
      const newDocument = {
        id: `doc_${Date.now()}`,
        fileName: processed.metadata.fileName,
        fileType: processed.metadata.fileType,
        fileSize: processed.metadata.fileSize || processed.metadata.compressedSize,
        fileData: processed.data,
        uploadedAt: processed.metadata.uploadedAt,
        relatedTo: relatedTo,
        relatedType: relatedType
      };

      // Persist to storage (add to ALL documents array)
      setAllDocuments(prev => [...prev, newDocument]);

      return newDocument;
    } catch (error) {
      throw error;
    } finally {
      setUploading(false);
    }
  }, [relatedTo, relatedType, setAllDocuments]); // Dependencies are stable

  // CRITICAL: Memoized delete function
  const deleteFile = useCallback((documentId) => {
    setAllDocuments(prev => prev.filter(doc => doc.id !== documentId));
  }, [setAllDocuments]);

  // CRITICAL: Memoized rename function
  const renameFile = useCallback((documentId, newFileName) => {
    setAllDocuments(prev => prev.map(doc =>
      doc.id === documentId ? { ...doc, fileName: newFileName } : doc
    ));
  }, [setAllDocuments]);

  // CRITICAL: Memoized download function
  const downloadFile = useCallback((document) => {
    downloadFileUtil(document.fileData, document.fileName);
  }, []); // No dependencies - utility function doesn't change

  return {
    documents,        // Filtered documents for current item
    uploading,        // Upload in progress
    uploadFile,       // Memoized upload function
    deleteFile,       // Memoized delete function
    renameFile,       // Memoized rename function
    downloadFile      // Memoized download function
  };
};

export default useFileUpload;
